import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';

const PdfViewer = dynamic(() => import('@/components/PdfViewer'), {
  ssr: false,
  loading: () => (
    <div className="h-96 flex items-center justify-center bg-slate-100">
      <span className="text-gray-500">Cargando PDF...</span>
    </div>
  ),
});

interface Documento {
  id: string;
  nombre_archivo: string;
  tipo: string;
  estado: 'pendiente' | 'procesado_ia' | 'contabilizado' | 'rechazado';
  datos_ia?: {
    total?: number;
    baseImponible?: number;
    cuotaIva?: number;
    proveedor?: string;
    fechaFactura?: string;
    nivelConfianza?: number;
  };
  procesado_ia: boolean;
  url_archivo: string;
  fecha_subida: string;
  fecha_procesamiento?: string;
}

interface Cliente {
  id: string;
  razon_social: string;
  nombre_comercial?: string;
}

export default function DocumentosCliente() {
  const router = useRouter();
  const { user } = useAuth();
  const { clienteId } = router.query;
  
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [processingDoc, setProcessingDoc] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<Documento | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ type: 'ia' | 'manual'; docId: string } | null>(null);

  // Cargar cliente y documentos
  useEffect(() => {
    if (clienteId) {
      cargarDatos();
    }
  }, [clienteId]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar info del cliente
      const clienteRes = await fetch(`/api/clientes/${clienteId}`);
      if (clienteRes.ok) {
        const clienteData = await clienteRes.json();
        setCliente(clienteData.data);
      }

      // Cargar documentos del cliente
      const docsRes = await fetch(`/api/documents?clienteId=${clienteId}&estados=pendiente,procesado_ia`);
      if (!docsRes.ok) throw new Error('Error al cargar documentos');
      const docsData = await docsRes.json();
      setDocumentos(docsData.data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Procesar documento con IA
  const procesarConIA = async (docId: string) => {
    try {
      setProcessingDoc(docId);
      const response = await fetch('/api/ai/verify-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: docId }),
      });

      if (!response.ok) throw new Error('Error al procesar con IA');
      
      const result = await response.json();
      
      // Actualizar estado local
      setDocumentos((prev) =>
        prev.map((d) =>
          d.id === docId
            ? { ...d, estado: 'procesado_ia', datos_ia: result.data, procesado_ia: true }
            : d
        )
      );
      
      toast.success('Documento procesado con IA');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al procesar con IA');
    } finally {
      setProcessingDoc(null);
      setConfirmModal(null);
    }
  };

  // Contabilizar manualmente
  const contabilizarManual = async (docId: string) => {
    try {
      setProcessingDoc(docId);
      
      // Abrir modal/formulario de contabilización manual
      router.push(`/asesor/contabilizacion-manual?documentoId=${docId}`);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al abrir contabilización manual');
    } finally {
      setProcessingDoc(null);
      setConfirmModal(null);
    }
  };

  // Contabilizar documento ya procesado por IA
  const contabilizarDocumento = async (docId: string) => {
    try {
      setProcessingDoc(docId);
      const response = await fetch('/api/documents/post-automatically', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentIds: [docId] }),
      });

      if (!response.ok) throw new Error('Error al contabilizar');
      
      // Remover de la lista
      setDocumentos((prev) => prev.filter((doc) => doc.id !== docId));
      
      toast.success('Documento contabilizado correctamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al contabilizar');
    } finally {
      setProcessingDoc(null);
    }
  };

  // Formato de moneda
  const formatMoney = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Formato de fecha
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  // Estadísticas
  const stats = {
    pendientes: documentos.filter((d) => d.estado === 'pendiente').length,
    conIA: documentos.filter((d) => d.estado === 'procesado_ia').length,
    total: documentos.length,
  };

  return (
    <Layout rol="asesor">
      <Head>
        <title>{cliente?.razon_social || 'Cliente'} - Documentos</title>
      </Head>

      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.push('/asesor/dashboard-contabilizacion')}
            >
              <span className="material-symbols-outlined">arrow_back</span>
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-semibold">{cliente?.razon_social}</h1>
              {cliente?.nombre_comercial && (
                <p className="text-gray-500">{cliente.nombre_comercial}</p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-2">
            <Badge variant="outline" className="px-3 py-1">
              <span className="font-semibold">{stats.pendientes}</span> sin procesar
            </Badge>
            <Badge variant="secondary" className="px-3 py-1">
              <span className="font-semibold">{stats.conIA}</span> con IA
            </Badge>
          </div>
        </div>

        {/* Lista de documentos */}
        <div className="space-y-4">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-20" />
                  </CardContent>
                </Card>
              ))
            : documentos.map((doc) => (
                <Card
                  key={doc.id}
                  className={cn(
                    'transition-all',
                    doc.estado === 'procesado_ia' && 'border-blue-300 bg-blue-50/30'
                  )}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Info del documento */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-gray-400">description</span>
                          <span className="font-medium">{doc.nombre_archivo}</span>
                          <Badge
                            variant={
                              doc.estado === 'pendiente'
                                ? 'outline'
                                : doc.estado === 'procesado_ia'
                                ? 'secondary'
                                : 'default'
                            }
                          >
                            {doc.estado === 'pendiente' && 'Pendiente'}
                            {doc.estado === 'procesado_ia' && 'En revisión (IA)'}
                          </Badge>
                        </div>

                        <div className="text-sm text-gray-500">
                          Subido: {formatDate(doc.fecha_subida)}
                        </div>

                        {/* Datos de IA si existen */}
                        {doc.datos_ia && (
                          <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Proveedor:</span>
                              <span className="font-medium">{doc.datos_ia.proveedor || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Fecha:</span>
                              <span className="font-medium">{doc.datos_ia.fechaFactura || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Base imponible:</span>
                              <span className="font-medium">{formatMoney(doc.datos_ia.baseImponible)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>IVA:</span>
                              <span className="font-medium">{formatMoney(doc.datos_ia.cuotaIva)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-1">
                              <span>Total:</span>
                              <span className="font-semibold">{formatMoney(doc.datos_ia.total)}</span>
                            </div>
                            {doc.datos_ia.nivelConfianza && (
                              <div className="flex justify-between text-xs">
                                <span>Confianza IA:</span>
                                <span
                                  className={cn(
                                    doc.datos_ia.nivelConfianza >= 0.8
                                      ? 'text-green-600'
                                      : doc.datos_ia.nivelConfianza >= 0.6
                                      ? 'text-amber-600'
                                      : 'text-red-600'
                                  )}
                                >
                                  {Math.round(doc.datos_ia.nivelConfianza * 100)}%
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Acciones */}
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        {doc.estado === 'pendiente' && (
                          <>
                            <Button
                              onClick={() => setConfirmModal({ type: 'ia', docId: doc.id })}
                              disabled={processingDoc === doc.id}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                            >
                              <span className="material-symbols-outlined text-sm mr-2">psychology</span>
                              {processingDoc === doc.id ? 'Procesando...' : 'Procesar con IA'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setConfirmModal({ type: 'manual', docId: doc.id })}
                              disabled={processingDoc === doc.id}
                              className="w-full"
                            >
                              <span className="material-symbols-outlined text-sm mr-2">edit_note</span>
                              Contabilizar manual
                            </Button>
                          </>
                        )}

                        {doc.estado === 'procesado_ia' && (
                          <>
                            <Button
                              onClick={() => contabilizarDocumento(doc.id)}
                              disabled={processingDoc === doc.id}
                              className="w-full bg-green-600 hover:bg-green-700"
                            >
                              <span className="material-symbols-outlined text-sm mr-2">check_circle</span>
                              {processingDoc === doc.id ? 'Contabilizando...' : 'Confirmar y contabilizar'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => router.push(`/asesor/contabilizacion-manual?documentoId=${doc.id}`)}
                              className="w-full"
                            >
                              <span className="material-symbols-outlined text-sm mr-2">edit</span>
                              Editar manualmente
                            </Button>
                          </>
                        )}

                        <Button
                          variant="ghost"
                          onClick={() => setViewingDoc(doc)}
                          className="w-full"
                        >
                          <span className="material-symbols-outlined text-sm mr-2">visibility</span>
                          Ver documento
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>

        {/* Empty state */}
        {!loading && documentos.length === 0 && (
          <div className="text-center py-12">
            <span className="material-symbols-outlined text-6xl text-gray-300">task_alt</span>
            <h3 className="mt-4 text-lg font-medium">No hay documentos pendientes</h3>
            <p className="text-gray-500">Todos los documentos han sido procesados</p>
          </div>
        )}

        {/* Modal de confirmación */}
        <Dialog open={!!confirmModal} onOpenChange={() => setConfirmModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmModal?.type === 'ia' ? 'Procesar con IA' : 'Contabilizar manual'}
              </DialogTitle>
              <DialogDescription>
                {confirmModal?.type === 'ia'
                  ? 'La IA extraerá los datos automáticamente. Podrás revisar antes de contabilizar.'
                  : 'Se abrirá el formulario de contabilización manual.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmModal(null)}>Cancelar</Button>
              <Button
                onClick={() => {
                  if (confirmModal?.type === 'ia') {
                    procesarConIA(confirmModal.docId);
                  } else if (confirmModal?.type === 'manual') {
                    contabilizarManual(confirmModal.docId);
                  }
                }}
              >
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de vista previa */}
        <Dialog open={!!viewingDoc} onOpenChange={() => setViewingDoc(null)}>
          <DialogContent className="max-w-4xl h-[80vh]">
            <DialogHeader>
              <DialogTitle>{viewingDoc?.nombre_archivo}</DialogTitle>
            </DialogHeader>
            {viewingDoc?.url_archivo && (
              <div className="h-full">
                <PdfViewer fileUrl={viewingDoc.url_archivo} />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
